import { useState, useEffect, useMemo } from "react";
import React from "react";
import {
  Card,
  CardHeader,
  Table,
  Container,
  Row,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  FormGroup,
} from "reactstrap";
import Select from "react-select";
import { reportmanageService } from "../../../services/reportmanageService";
import "../../../assets/css/custom.css";
import Header from "components/Headers/Header.js";
import { Formloader } from "components/Loader/PageLoader.js";

const TurnaroundReport = (props) => {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientFilter, setClientFilter] = useState(null);
  const [managerFilter, setManagerFilter] = useState(null);

  // Job drilldown (open / on_hold / delayed)
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [jobModalTitle, setJobModalTitle] = useState("");
  const [jobModalData, setJobModalData] = useState([]);

  // Due-date-changes drilldown
  const [ddModalOpen, setDdModalOpen] = useState(false);
  const [ddModalTitle, setDdModalTitle] = useState("");
  const [ddModalData, setDdModalData] = useState([]);

  const load = () => {
    setLoading(true);
    reportmanageService.getTurnaroundReport("", "").then((response) => {
      if (response && response.data && response.data.data) {
        setAllRows(response.data.data);
      } else {
        setAllRows([]);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
  }, []);

  const managerOptions = useMemo(() => {
    const seen = new Map();
    allRows.forEach((r) => {
      if (!seen.has(r.manager_id)) {
        seen.set(r.manager_id, { value: r.manager_id, label: r.manager_name });
      }
    });
    return [{ value: "", label: "All Managers" }, ...Array.from(seen.values())];
  }, [allRows]);

  const clientOptions = useMemo(() => {
    const seen = new Set();
    const list = [];
    allRows.forEach((r) => {
      if (!seen.has(r.client_name)) {
        seen.add(r.client_name);
        list.push({ value: r.client_name, label: r.client_name });
      }
    });
    return [{ value: "", label: "All Clients" }, ...list];
  }, [allRows]);

  const rows = useMemo(() => {
    return allRows.filter((r) => {
      if (managerFilter && managerFilter.value !== "" && r.manager_id !== managerFilter.value) return false;
      if (clientFilter && clientFilter.value !== "" && r.client_name !== clientFilter.value) return false;
      return true;
    });
  }, [allRows, managerFilter, clientFilter]);

  const openJobsDrilldown = (kpi, row, label) => {
    setJobModalTitle(`${label} — ${row.client_name} (Manager: ${row.manager_name})`);
    setJobModalData([]);
    setJobModalOpen(true);
    reportmanageService
      .getTurnaroundJobs(kpi, row.manager_id, row.client_name, 1, 100)
      .then((response) => {
        if (response && response.data && response.data.data) {
          setJobModalData(response.data.data.data || []);
        }
      });
  };

  const openDueDateChangesDrilldown = (row) => {
    setDdModalTitle(`Due Date Changes — ${row.client_name} (Manager: ${row.manager_name})`);
    setDdModalData([]);
    setDdModalOpen(true);
    reportmanageService
      .getTurnaroundDueDateChanges(row.manager_id, row.client_name, 1, 100)
      .then((response) => {
        if (response && response.data && response.data.data) {
          setDdModalData(response.data.data.data || []);
        }
      });
  };

  const formatDate = (val) => {
    if (!val) return "";
    if (typeof val === "string" && (val.startsWith("0000-00-00") || val === "0")) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    if (d.getFullYear() <= 1970) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const formatDateTime = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
  };

  const linkStyle = {
    cursor: "pointer",
    color: "#5e72e4",
    fontWeight: 600,
    textDecoration: "underline",
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <h2>Turnaround Report (Outsourcing Clients)</h2>
          </Col>
        </Row>

        <div className="col" style={{ padding: "0%" }}>
          <Card className="shadow">
            <CardHeader>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <label>Manager</label>
                    <Select
                      options={managerOptions}
                      value={managerFilter}
                      onChange={(opt) => setManagerFilter(opt)}
                      placeholder="All Managers"
                      isClearable
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <label>Client</label>
                    <Select
                      options={clientOptions}
                      value={clientFilter}
                      onChange={(opt) => setClientFilter(opt)}
                      placeholder="All Clients"
                      isClearable
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardHeader>

            {loading ? <Formloader /> : null}

            <Table className="align-items-center table-flush" responsive hover>
              <thead className="thead-light">
                <tr>
                  <th scope="col" className="whitespace">Manager</th>
                  <th scope="col" className="whitespace">Client Name</th>
                  <th scope="col" className="whitespace text-right">Open Jobs</th>
                  <th scope="col" className="whitespace text-right">On Hold Jobs</th>
                  <th scope="col" className="whitespace text-right">Delayed &gt; 7 days from LIRD</th>
                  <th scope="col" className="whitespace text-right">No. of Jobs with No Due Date</th>
                  <th scope="col" className="whitespace text-right">No. of Due Date Changes</th>
                </tr>
              </thead>
              <tbody>
                {rows && rows.length ? (
                  rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "blue-row" : "orange-row"}>
                      <td className="whitespace">{row.manager_name}</td>
                      <td className="whitespace">{row.client_name}</td>
                      <td className="text-right">
                        {row.open_jobs > 0 ? (
                          <span style={linkStyle} onClick={() => openJobsDrilldown("open", row, "Open Jobs")}>
                            {row.open_jobs}
                          </span>
                        ) : (
                          <span>0</span>
                        )}
                      </td>
                      <td className="text-right">
                        {row.on_hold > 0 ? (
                          <span style={linkStyle} onClick={() => openJobsDrilldown("on_hold", row, "On Hold Jobs")}>
                            {row.on_hold}
                          </span>
                        ) : (
                          <span>0</span>
                        )}
                      </td>
                      <td className="text-right">
                        {row.delayed_gt7 > 0 ? (
                          <span style={linkStyle} onClick={() => openJobsDrilldown("delayed", row, "Delayed Jobs > 7 days from LIRD")}>
                            {row.delayed_gt7}
                          </span>
                        ) : (
                          <span>0</span>
                        )}
                      </td>
                      <td className="text-right">
                        {row.no_due_date > 0 ? (
                          <span style={linkStyle} onClick={() => openJobsDrilldown("no_due_date", row, "Jobs with No Due Date")}>
                            {row.no_due_date}
                          </span>
                        ) : (
                          <span>0</span>
                        )}
                      </td>
                      <td className="text-right">
                        {row.due_date_changes > 0 ? (
                          <span style={linkStyle} onClick={() => openDueDateChangesDrilldown(row)}>
                            {row.due_date_changes}
                          </span>
                        ) : (
                          <span>0</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No data
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>
        </div>
      </Container>

      {/* Jobs drilldown modal */}
      <Modal isOpen={jobModalOpen} toggle={() => setJobModalOpen(!jobModalOpen)} size="xl" scrollable>
        <ModalHeader toggle={() => setJobModalOpen(!jobModalOpen)}>{jobModalTitle}</ModalHeader>
        <ModalBody>
          <Table responsive hover>
            <thead className="thead-light">
              <tr>
                <th>Job ID</th>
                <th>Client</th>
                <th>Sub Client</th>
                <th>Status</th>
                <th>Doc Received</th>
                <th>Raised Date</th>
                <th>Due Date</th>
                <th className="text-right">Days since LIRD</th>
              </tr>
            </thead>
            <tbody>
              {jobModalData && jobModalData.length ? (
                jobModalData.map((j) => (
                  <tr key={j.id}>
                    <td>{j.id}</td>
                    <td>{j.client_name}</td>
                    <td>{j.sub_client}</td>
                    <td>{j.status_name}</td>
                    <td>{formatDate(j.doc_received_date)}</td>
                    <td>{formatDate(j.raised_date)}</td>
                    <td>{formatDate(j.due_date)}</td>
                    <td className="text-right">{j.days_since_lird}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">No records</td>
                </tr>
              )}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>

      {/* Due-date-changes drilldown modal */}
      <Modal isOpen={ddModalOpen} toggle={() => setDdModalOpen(!ddModalOpen)} size="xl" scrollable>
        <ModalHeader toggle={() => setDdModalOpen(!ddModalOpen)}>{ddModalTitle}</ModalHeader>
        <ModalBody>
          <Table responsive hover>
            <thead className="thead-light">
              <tr>
                <th>Job ID</th>
                <th>Client</th>
                <th>Sub Client</th>
                <th>Previous Due Date</th>
                <th>Current Due Date</th>
                <th>Changed By</th>
                <th>Changed At</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {ddModalData && ddModalData.length ? (
                ddModalData.map((c) => (
                  <tr key={c.history_id}>
                    <td>{c.task_id}</td>
                    <td>{c.client_name}</td>
                    <td>{c.sub_client}</td>
                    <td>{formatDate(c.previous_due_date)}</td>
                    <td>{formatDate(c.current_due_date)}</td>
                    <td>{c.changed_by_name}</td>
                    <td>{formatDateTime(c.changed_at)}</td>
                    <td>{c.message}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">No records</td>
                </tr>
              )}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>
    </>
  );
};

export default TurnaroundReport;
